import {
  SearchBar,
  Select,
  Button,
  type Column,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import {
  crmWebhookLogs,
  type CrmWebhookLog,
  type CrmWebhookLogStatus,
} from "../../api/crm-webhook-logs.service.js";
import { crmPackageMappings } from "../../api/crm-package-mappings.service.js";
import { SkipProductsModal, type SkipProduct } from "../../components/SkipProductsModal/index.js";
import { ConfirmationModal } from "../../components/Modal/ConfirmationModal.js";
import { DataTable } from "../../components/DataTable/DataTable.js";
import { PaginationControls } from "../../components/PaginationControls/PaginationControls.js";
import LoadingState from "../../components/LoadingState/index.js";
import EmptyState from "../../components/EmptyState/index.js";
import { formatDateTimeShort } from "../../utils/dateFormatter.js";
import styles from "./CrmWebhookLogs.module.css";

type CrmWebhookLogListContext = {
  refetchTrigger?: number;
  setListError?: (error: Error | null) => void;
  setToast?: (toast: { message: string; type: ToastType } | null) => void;
};

function CrmWebhookLogList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const context = useOutletContext<CrmWebhookLogListContext>();
  const [displayedLogs, setDisplayedLogs] = useState<CrmWebhookLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const debouncedSearchTerm = searchParams.get("search") || "";
  const initialFilter =
    searchParams.get("status") === "failed" && searchParams.get("errorCode")
      ? `failed:${searchParams.get("errorCode")}`
      : searchParams.get("handled") === "true"
        ? "handled"
        : searchParams.get("status") || "not_handled";
  const [statusFilter, setStatusFilter] = useState<string>(initialFilter);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [retrying, setRetrying] = useState(false);
  const [retryAllModalOpen, setRetryAllModalOpen] = useState(false);
  const [markingHandled, setMarkingHandled] = useState(false);
  const [skipModalOpen, setSkipModalOpen] = useState(false);
  const [skipProducts, setSkipProducts] = useState<SkipProduct[]>([]);
  const [skippingProducts, setSkippingProducts] = useState(false);
  const selectAllRef = useRef<HTMLInputElement>(null);
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);
  const [itemsPerPage, setItemsPerPage] = useState(Number(searchParams.get("pageSize")) || 25);

  function updateURL(updates: Record<string, string>) {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== "") {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  }

  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  function handleSearchChange(value: string) {
    setSearchTerm(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      updateURL({ search: value, page: "1" });
    }, 300);
  }

  useEffect(() => {
    const pageFromURL = Number(searchParams.get("page")) || 1;
    const pageSizeFromURL = Number(searchParams.get("pageSize")) || 25;
    if (pageFromURL !== currentPage) setCurrentPage(pageFromURL);
    if (pageSizeFromURL !== itemsPerPage) setItemsPerPage(pageSizeFromURL);
  }, [searchParams]);

  useEffect(() => {
    loadLogs();
  }, [currentPage, itemsPerPage, debouncedSearchTerm, statusFilter]);

  useEffect(() => {
    if (context.refetchTrigger !== undefined && context.refetchTrigger > 0) {
      loadLogs();
    }
  }, [context.refetchTrigger]);

  useEffect(() => {
    if (!selectAllRef.current) return;
    const allSelected =
      displayedLogs.length > 0 && displayedLogs.every(log => selectedIds.has(log.id));
    const someSelected =
      displayedLogs.length > 0 && displayedLogs.some(log => selectedIds.has(log.id));
    selectAllRef.current.checked = allSelected;
    selectAllRef.current.indeterminate = someSelected && !allSelected;
  }, [selectedIds, displayedLogs]);

  async function loadLogs() {
    try {
      setLoading(true);
      setError(null);
      if (context.setListError) {
        context.setListError(null);
      }
      const offset = (currentPage - 1) * itemsPerPage;
      const filters = getActiveFilters();

      const response = await crmWebhookLogs.getAll({
        ...filters,
        status: filters.status as CrmWebhookLogStatus | undefined,
        limit: itemsPerPage,
        offset: offset,
      });
      setDisplayedLogs(response.logs);
      setTotal(response.total);
    } catch (err) {
      const loadError = err instanceof Error ? err : new Error("Failed to load logs");
      setError(loadError);
      if (context.setListError) {
        context.setListError(loadError);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleRetrySelected() {
    setRetrying(true);
    try {
      const result = await crmWebhookLogs.retryBulk(Array.from(selectedIds));
      const successCount = result.results.filter(r => r.status === "created").length;
      const failCount = result.results.filter(r => r.status === "failed").length;
      context.setToast?.({
        message: `Retry complete: ${successCount} succeeded, ${failCount} failed`,
        type: failCount > 0 ? "warning" : "success",
      });
      setSelectedIds(new Set());
      loadLogs();
    } catch (err) {
      context.setToast?.({
        message: `Retry failed: ${err instanceof Error ? err.message : "Unknown error"}`,
        type: "error",
      });
    } finally {
      setRetrying(false);
    }
  }

  async function handleMarkHandledSelected() {
    setMarkingHandled(true);
    try {
      const result = await crmWebhookLogs.markHandledBulk(Array.from(selectedIds));
      context.setToast?.({
        message: `Resolved ${result.updated} log${result.updated !== 1 ? "s" : ""}`,
        type: "success",
      });
      setSelectedIds(new Set());
      loadLogs();
    } catch (err) {
      context.setToast?.({
        message: `Failed to resolve: ${err instanceof Error ? err.message : "Unknown error"}`,
        type: "error",
      });
    } finally {
      setMarkingHandled(false);
    }
  }

  function getActiveFilters() {
    let filterStatus: string | undefined;
    let filterErrorCode: string | undefined;
    let filterHandled: boolean | undefined;

    if (statusFilter === "not_handled") {
      filterHandled = false;
    } else if (statusFilter === "handled") {
      filterHandled = true;
    } else if (statusFilter.startsWith("failed:")) {
      filterStatus = "failed";
      filterErrorCode = statusFilter.slice(7);
      filterHandled = false;
    } else if (statusFilter && statusFilter !== "") {
      filterStatus = statusFilter;
      filterHandled = false;
    }

    return {
      status: filterStatus,
      errorCode: filterErrorCode,
      handled: filterHandled,
      search: debouncedSearchTerm || undefined,
    };
  }

  async function handleRetryAllConfirm() {
    const result = await crmWebhookLogs.retryAll(getActiveFilters());
    context.setToast?.({
      message: `Retried ${result.total}: ${result.created} created, ${result.duplicate} duplicate, ${result.failed} failed, ${result.dropped} dropped, ${result.unchanged} unchanged`,
      type: result.failed > 0 ? "warning" : "success",
    });
    setRetryAllModalOpen(false);
    loadLogs();
  }

  function handleOpenSkipModal() {
    const selectedLogs = displayedLogs.filter(log => selectedIds.has(log.id));
    const productCounts = new Map<string, number>();

    for (const log of selectedLogs) {
      const rawPayload = log.rawPayload as Record<string, unknown>;
      const productId = (rawPayload.productId || rawPayload.productCode) as string | undefined;
      if (productId) {
        productCounts.set(productId, (productCounts.get(productId) || 0) + 1);
      }
    }

    const products: SkipProduct[] = Array.from(productCounts.entries()).map(
      ([productId, logCount]) => ({ productId, logCount }),
    );

    if (products.length === 0) {
      context.setToast?.({
        message: "No product IDs found in selected logs",
        type: "warning",
      });
      return;
    }

    setSkipProducts(products);
    setSkipModalOpen(true);
  }

  async function handleConfirmSkip() {
    setSkippingProducts(true);
    try {
      const entries = skipProducts.map(p => ({ crmPackageId: p.productId }));
      const result = await crmPackageMappings.bulkSkip(entries);

      await crmWebhookLogs.markHandledBulk(Array.from(selectedIds));

      context.setToast?.({
        message: `Created ${result.created} skip mapping${result.created !== 1 ? "s" : ""}${result.skipped > 0 ? ` (${result.skipped} already existed)` : ""}, resolved ${selectedIds.size} logs`,
        type: "success",
      });

      setSelectedIds(new Set());
      setSkipModalOpen(false);
      setSkipProducts([]);
      loadLogs();
    } catch (err) {
      context.setToast?.({
        message: `Failed to skip products: ${err instanceof Error ? err.message : "Unknown error"}`,
        type: "error",
      });
    } finally {
      setSkippingProducts(false);
    }
  }

  const totalPages = Math.ceil(total / itemsPerPage);
  const offset = (currentPage - 1) * itemsPerPage;
  const startIndex = offset;
  const endIndex = offset + displayedLogs.length;

  function handleLogClick(log: CrmWebhookLog) {
    navigate(`/fulfilment/crm-webhook-logs/${log.id}`);
  }

  function getStatusBadgeClass(status: string) {
    switch (status) {
      case "created":
        return styles.statusCreated;
      case "duplicate":
        return styles.statusDuplicate;
      case "failed":
        return styles.statusFailed;
      case "dropped":
        return styles.statusDropped;
      default:
        return "";
    }
  }

  function handleSelectAll() {
    const allSelected = displayedLogs.every(log => selectedIds.has(log.id));
    if (allSelected) {
      const newSelected = new Set(selectedIds);
      for (const log of displayedLogs) {
        newSelected.delete(log.id);
      }
      setSelectedIds(newSelected);
    } else {
      const newSelected = new Set(selectedIds);
      for (const log of displayedLogs) {
        newSelected.add(log.id);
      }
      setSelectedIds(newSelected);
    }
  }

  const checkboxColumn: Column<CrmWebhookLog> = {
    header: (
      <input
        ref={selectAllRef}
        type="checkbox"
        onChange={handleSelectAll}
        onClick={e => e.stopPropagation()}
      />
    ) as unknown as string,
    accessor: "id",
    render: (_value, row) => (
      <input
        type="checkbox"
        checked={selectedIds.has(row.id)}
        onChange={e => {
          e.stopPropagation();
          const newSelected = new Set(selectedIds);
          if (e.target.checked) {
            newSelected.add(row.id);
          } else {
            newSelected.delete(row.id);
          }
          setSelectedIds(newSelected);
        }}
        onClick={e => e.stopPropagation()}
      />
    ),
  };

  const columns: Column<CrmWebhookLog>[] = [
    {
      header: "Timestamp",
      accessor: "createdAt",
      render: (_value, row) => (
        <span style={{ whiteSpace: "nowrap" }}>
          {row.createdAt ? formatDateTimeShort(row.createdAt) : "-"}
        </span>
      ),
    },
    {
      header: "Product Code",
      accessor: "rawPayload",
      render: (_value, row) => {
        const productCode = (row.rawPayload as Record<string, unknown>)?.productCode as
          | string
          | undefined;
        return productCode ? (
          <span style={{ fontFamily: "monospace", fontSize: "0.75rem", whiteSpace: "nowrap" }}>
            {productCode.substring(0, 13)}...
          </span>
        ) : (
          <span style={{ color: "var(--color-text-secondary)" }}>-</span>
        );
      },
    },
    {
      header: "CRM Firm ID",
      accessor: "crmFirmId",
      render: (_value, row) =>
        row.crmFirmId ? (
          <span style={{ fontFamily: "monospace", fontSize: "0.75rem", whiteSpace: "nowrap" }}>
            {row.crmFirmId.substring(0, 13)}...
          </span>
        ) : (
          <span style={{ color: "var(--color-text-secondary)" }}>-</span>
        ),
    },
    {
      header: "Region",
      accessor: "regionName",
      render: (_value, row) => (
        <span style={{ color: row.regionName ? "inherit" : "var(--color-text-secondary)" }}>
          {row.regionName || "-"}
        </span>
      ),
    },
    {
      header: "Status",
      accessor: "status",
      render: (_value, row) => (
        <span className={`${styles.statusBadge} ${getStatusBadgeClass(row.status)}`}>
          {row.status}
        </span>
      ),
    },
    {
      header: "Subscription",
      accessor: "subscriptionId",
      render: (_value, row) =>
        row.subscriptionId ? (
          <span style={{ fontFamily: "monospace", fontSize: "0.75rem", whiteSpace: "nowrap" }}>
            {row.subscriptionId.substring(0, 8)}...
          </span>
        ) : (
          <span style={{ color: "var(--color-text-secondary)" }}>-</span>
        ),
    },
  ];

  const allColumns = [checkboxColumn, ...columns];

  if (loading && displayedLogs.length === 0) {
    return <LoadingState message="Loading webhook logs..." />;
  }

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load logs"
        message={error.message}
        action={{
          label: "Retry",
          onClick: loadLogs,
        }}
      />
    );
  }

  return (
    <>
      <div className={styles.searchSection}>
        <SearchBar
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by Correlation ID, CRM Firm ID, or Product Code..."
          onClear={() => handleSearchChange("")}
        />
      </div>

      <div className={styles.controlsSection}>
        <div className={styles.filterSection}>
          <Select
            label="Status"
            value={statusFilter}
            onChange={e => {
              const newStatus = e.target.value;
              setStatusFilter(newStatus);
              setCurrentPage(1);
              if (newStatus === "not_handled") {
                updateURL({ status: "", errorCode: "", handled: "false", page: "1" });
              } else if (newStatus === "handled") {
                updateURL({ status: "", errorCode: "", handled: "true", page: "1" });
              } else if (newStatus.startsWith("failed:")) {
                updateURL({
                  status: "failed",
                  errorCode: newStatus.slice(7),
                  handled: "",
                  page: "1",
                });
              } else {
                updateURL({ status: newStatus, errorCode: "", handled: "", page: "1" });
              }
            }}
            options={[
              { value: "not_handled", label: "Open" },
              { value: "handled", label: "Resolved" },
              { value: "created", label: "Created" },
              { value: "duplicate", label: "Duplicate" },
              { value: "failed", label: "Failed" },
              { value: "failed:package_mapping_missing", label: "Failed: No package mapping" },
              { value: "failed:firm_not_found", label: "Failed: Firm not found" },
              { value: "failed:no_active_package", label: "Failed: No active package" },
              { value: "dropped", label: "Dropped" },
              { value: "", label: "All" },
            ]}
            size="small"
          />
        </div>
        <div className={styles.pageSizeSelector}>
          <Select
            label="Items per page"
            value={itemsPerPage}
            onChange={e => {
              const newSize = Number(e.target.value);
              setItemsPerPage(newSize);
              setCurrentPage(1);
              updateURL({ pageSize: String(newSize), page: "1" });
            }}
            options={[
              { value: 10, label: "10" },
              { value: 25, label: "25" },
              { value: 50, label: "50" },
              { value: 100, label: "100" },
            ]}
            size="small"
          />
        </div>
      </div>

      {selectedIds.size === 0 && total > 0 && (
        <div className={styles.bulkActions}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => setRetryAllModalOpen(true)}
          >
            {`Retry All (${total})`}
          </Button>
        </div>
      )}

      {retryAllModalOpen && (
        <ConfirmationModal
          title="Retry All Logs"
          message={`This will retry ${total} webhook logs matching the current filters.`}
          warningMessage="Each log will be replayed. New log entries are created only when the outcome changes."
          confirmButtonText="Retry All"
          size="small"
          onClose={() => setRetryAllModalOpen(false)}
          onConfirm={handleRetryAllConfirm}
        />
      )}

      {selectedIds.size > 0 && (
        <div className={styles.bulkActions}>
          <span>{selectedIds.size} selected</span>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleRetrySelected}
            disabled={retrying || markingHandled}
          >
            {retrying ? "Retrying..." : `Retry Selected (${selectedIds.size})`}
          </Button>
          <Button
            variant="outlined"
            color="default"
            size="small"
            onClick={handleOpenSkipModal}
            disabled={retrying || markingHandled || skippingProducts}
          >
            Skip Products
          </Button>
          <Button
            variant="outlined"
            color="default"
            size="small"
            onClick={handleMarkHandledSelected}
            disabled={retrying || markingHandled || skippingProducts}
          >
            {markingHandled ? "Resolving..." : `Resolve (${selectedIds.size})`}
          </Button>
        </div>
      )}

      <div className={styles.contentSection}>
        {displayedLogs.length === 0 ? (
          <EmptyState
            variant="empty"
            title="No webhook logs found"
            message={
              searchTerm
                ? "Try adjusting your search criteria"
                : "No CRM webhook calls have been logged yet"
            }
          />
        ) : (
          <DataTable
            columns={allColumns}
            data={displayedLogs}
            onRowClick={handleLogClick}
            rowKey="id"
          />
        )}

        {!loading && !error && displayedLogs.length > 0 && (
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            startIndex={startIndex}
            endIndex={endIndex}
            totalCount={total}
            itemName="logs"
            onPageChange={page => {
              setCurrentPage(page);
              updateURL({ page: String(page) });
            }}
          />
        )}
      </div>

      <SkipProductsModal
        products={skipProducts}
        isOpen={skipModalOpen}
        onClose={() => {
          setSkipModalOpen(false);
          setSkipProducts([]);
        }}
        onConfirm={handleConfirmSkip}
        isSubmitting={skippingProducts}
      />
    </>
  );
}

export default CrmWebhookLogList;
